import { useCallback, useMemo } from 'react'
import { /*dateFormat, */numberFormat } from 'shared/util'
import {
	Box, BoxProps, Alert, Stack,
	Typography, Chip,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TableRowProps
} from "@mui/material"

import { ArticlesAuthor } from 'interface/article'

type CellValue = {
	code?: string | null
	text: string | number | null
	style?: string
	unit?: string
}
type ColumnDef = {
	key: string
	title: string
	align: 'left' | 'center' | 'right' | 'chips'
	hidden?: boolean
	expand?: boolean
	val: (record: ArticlesAuthor) => CellValue | CellValue[] | null | undefined
}

const columns: ColumnDef[] = [
	{ key: 'name', title: '名前', align: 'left', val: record => ({ text: record.authorName ?? null } as CellValue ) },
	{ key: 'journal_rank', title: '論文順位', align: 'right', val: record => record.globalRank ? { text: record.globalRank, unit: '位' } as CellValue : null },
	{ key: 'journal_first', title: '先頭', align: 'right', val: record => record.counts?.first ? { text: record.counts.first, unit: '件' } as CellValue : null },
	{ key: 'journal_last', title: '責任', align: 'right', val: record => record.counts?.last ? { text: record.counts.last, unit: '件' } as CellValue : null },
	{ key: 'journal_single', title: '単独', align: 'right', val: record => record.counts?.single ? { text: record.counts.single, unit: '件' } as CellValue : null },
	{ key: 'journal_count', title: '件数', align: 'right', val: record => record.counts?.total ? { text: record.counts.total, unit: '件' } as CellValue : null },
	{ key: 'age', title: '年齢', align: 'right', val: record => record.nenrei ? { text: record.nenrei, unit: '歳' } as CellValue : null },
	{ key: 'sex', title: '性別', align: 'center', val: record => (
		record.seibetuCode === '1' ? { text: '男性', style: 'male' } as CellValue :
		record.seibetuCode === '2' ? { text: '女性', style: 'female' } as CellValue : null
	)},
	{ key: 'buka', title: '部課', align: 'left', val: record => record.buka ? { code: record.buka.code, text: record.buka.name ?? null } as CellValue : null, expand: true },
	{ key: 'post', title: '役職', align: 'left', val: record => record.yakusyoku ? { code: record.yakusyoku.code, text: record.yakusyoku.name ?? null } as CellValue : null },
	{ key: 'sotukou', title: '卒業校', align: 'left', val: record => ({ text: record.sotukouName ?? null } as CellValue ) },
]

type TableHeaderRowProps = {
	columns: ColumnDef[]
} & TableRowProps
const TableHeaderRow = ({
	columns,
	...others
}: TableHeaderRowProps) => {
	return (
		<TableRow {...others}>
			{columns.filter(column => !column.hidden).map((column) => (
				<TableCell key={column.key} style={{ width: 0, whiteSpace: 'nowrap' }}>{column.title}</TableCell>
			))}
			<TableCell/>
		</TableRow>
	)  
}

const DisableFieldRenderer = () => (
	<TableCell aria-disabled style={{ background: 'GhostWhite' }}></TableCell>
)

type TextFieldRendererProps = {
	text: string
	unit: string | null | undefined
	align: ColumnDef['align']
	expand?: boolean
}
const TextFieldRenderer = ({
	text,
	unit,
	align,
	expand
}: TextFieldRendererProps) => text === '' ? <DisableFieldRenderer/> : (
	<TableCell
		sx={(expand === true) ? {
			width: '100%',
			textAlign: align
		} : {
			whiteSpace: "nowrap",
			textAlign: align
		}}
	>
		{text}
		{unit ? (
			<Typography
				variant='body2'
				component='span'
				fontSize='80%'
				color='text.secondary'
				marginLeft={0.5}
			>
				{unit}
			</Typography>
		) : <></>}
	</TableCell>
)

type ArrayFieldRendererProps = {
	authorId: number
	columnKey: string
	vals: CellValue[]
	expand?: boolean
}
const ArrayFieldRenderer = ({
	authorId,
	columnKey,
	vals,
	expand
}: ArrayFieldRendererProps) => vals.length === 0 ? <DisableFieldRenderer/> : (
	<TableCell
		sx={(expand === true) ? {
			width: '100%'
		} : undefined}
	>
		<Stack direction="row" spacing={0.5}>
			{vals.map((v, j) => (
				<Chip
					key={`${authorId}_${columnKey}_${j}`}
					size='small'
					label={(typeof v.text === 'number') ? numberFormat(v.text) : v.text}
				/>
			))}
		</Stack>
	</TableCell>
)

type AuthorsTableCellProps = {
	column: ColumnDef
	record: ArticlesAuthor
}
const AuthorsTableCell = ({
	column,
	record
}: AuthorsTableCellProps) => {
	const value = useMemo(() => column.val(record), [column, record])
	return (value === null || value === undefined) ? (
		<DisableFieldRenderer/>
	) : (column.align === 'chips') ? (
		<ArrayFieldRenderer
			authorId={record.authorId}
			columnKey={column.key}
			vals={Array.isArray(value) ? value : [value]}
			expand={column.expand}
		/>
	) : (
		<TextFieldRenderer
			text={(Array.isArray(value) ? value : [value])
				.flatMap(v => v.text ? ((typeof v.text === 'number') ? numberFormat(v.text) : v.text) : [])
				.join(', ')}
			unit={Array.isArray(value) ? (value.length > 0 ? value[0].unit : null) : value.unit}
			align={column.align}
			expand={column.expand}
		/>
	)
}

type AuthorsTableRowProps = {
	index: number
	columns: ColumnDef[]
	record: ArticlesAuthor
} & TableRowProps
const AuthorsTableRow = ({
	columns,
	record,
	...others
}: AuthorsTableRowProps) => (
	<TableRow {...others}>
		{columns.map(column => (
			<AuthorsTableCell
				key={`${record.kojinCode}_${column.key}`}
				column={column}
				record={record}
			/>
		))}
	</TableRow>
)

type AuthorsTableProps = {
	authors: ArticlesAuthor[]
	selectedAuthorId: number | null
	setSelectedAuthorId: (authorId: number | null) => void
} & BoxProps
export const AuthorsTable = ({
	authors,
	selectedAuthorId,
	setSelectedAuthorId,
	...others
}: AuthorsTableProps) => {
	const onClickedRow = useCallback(
		(author: ArticlesAuthor) => {
			setSelectedAuthorId(author.authorId === selectedAuthorId ? null : author.authorId)
		},
		[setSelectedAuthorId, selectedAuthorId]
	)
	const validColumns = useMemo(
		() => columns.filter(column => !column.hidden),
		[]
	)
	return (
		<Box {...others} position='relative'>
			{authors.length === 0 ? (
				<Box padding={1}><Alert severity="info">対象が見つかりません.</Alert></Box>
			) : (
				<TableContainer sx={{ width: '100%', height: '100%' }}>
					<Table aria-label="doctors table" stickyHeader size="small" >
						<TableHead>
							<TableHeaderRow columns={validColumns}/>
						</TableHead>
						<TableBody>
							{authors.map((record, index) => (
								<AuthorsTableRow
									key={`kol-record-${index}`}
									index={index}
									columns={validColumns}
									record={record}
									selected={selectedAuthorId === record.authorId}
									onClick={() => onClickedRow(record)}
									hover
									sx={{ cursor: 'pointer' }}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}